import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getUserNotificationSettings,
  updateUserNotificationSettings as updateUserNotificationSettingsApi,
  getOrgNotificationSettings,
  updateOrgNotificationSettings as updateOrgNotificationSettingsApi,
} from '@kirin/api-client/api/notification-settings';
import type {
  NotificationSettingsResponse,
  UpdateNotificationSettingsBody,
} from '@kirin/api-client/contracts/notification-settings';

/**
 * Hook to fetch user's personal notification settings.
 * Returns the user's configured minimum alert severity threshold.
 *
 * When feature flag is OFF, returns 'low' (show all notifications = current behavior).
 * When no setting exists, returns default 'high' for personal users.
 */
export function useUserNotificationSettings() {
  return useQuery({
    queryKey: queryKeys.notificationSettings.user(),
    queryFn: () => getUserNotificationSettings(apiClient),
    retry: retryHandlers.detail, // Don't retry on 404/auth errors
  });
}

/**
 * Hook to update user's personal notification settings.
 * Creates or updates the user's minimum alert severity threshold.
 */
export function useUpdateUserNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateNotificationSettingsBody) =>
      updateUserNotificationSettingsApi(apiClient, data),
    onSuccess: (data: NotificationSettingsResponse) => {
      // Update cache with new data
      queryClient.setQueryData(
        queryKeys.notificationSettings.user(),
        (old: NotificationSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}

/**
 * Hook to fetch organization notification settings.
 * Returns the organization's configured minimum alert severity threshold.
 *
 * Authorization: Owner, Manager, or Member (all can view).
 *
 * When feature flag is OFF, returns 'low' (show all notifications = current behavior).
 * When no setting exists, returns default 'actions_only' for organization context.
 */
export function useOrgNotificationSettings(organizationId: string) {
  return useQuery({
    queryKey: queryKeys.notificationSettings.organization(organizationId),
    queryFn: () => getOrgNotificationSettings(apiClient, organizationId),
    enabled: !!organizationId, // Only run query if organizationId exists
    retry: retryHandlers.detail, // Don't retry on 404/auth errors
  });
}

/**
 * Hook to update organization notification settings.
 * Creates or updates the organization's minimum alert severity threshold.
 *
 * Authorization: Owner or Manager only (members cannot modify).
 *
 * Note: This hook is instantiated even in personal context due to React's rules of hooks
 * (hooks must be called unconditionally). The mutation is only invoked when organizationId
 * is provided. When organizationId is empty, the hook exists but mutate() is never called.
 */
export function useUpdateOrgNotificationSettings(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateNotificationSettingsBody) => {
      // Fail-fast guard: prevent accidental calls with empty organizationId
      if (!organizationId) {
        return Promise.reject(
          new Error(
            'Cannot update organization settings: organizationId is required'
          )
        );
      }
      return updateOrgNotificationSettingsApi(apiClient, organizationId, data);
    },
    onSuccess: (data: NotificationSettingsResponse) => {
      // Update cache with new data
      queryClient.setQueryData(
        queryKeys.notificationSettings.organization(organizationId),
        (old: NotificationSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}
